import { useState } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader, PageContainer } from '@/components/layout/PageHeader'
import { Button, Badge, Card, CardContent } from '@/components/ui'
import { adminApi } from '@/modules/admin/api/admin.api'
import { formatDateTime } from '@/lib/formatters'

export default function EntitiesPage() {
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)

  const { data: entities, isLoading } = useQuery({
    queryKey: ['admin-entities'],
    queryFn: () => adminApi.getEntities(),
  })

  return (
    <PageContainer>
      <PageHeader
        title="Entités"
        description="Gestion des entités juridiques NexusCorp (multi-tenant)"
        breadcrumb={[{ label: 'Administration' }, { label: 'Entités' }]}
        actions={
          <Button size="sm" onClick={() => setShowCreateModal(true)}>
            + Nouvelle entité
          </Button>
        }
      />

      {/* Info multi-tenant */}
      <div className="mb-6 bg-[var(--blue-bg)] border border-[rgba(245,124,0,0.25)] rounded-xl p-4">
        <div className="flex items-start gap-3">
          <span className="text-[var(--blue-d)] text-lg">ℹ️</span>
          <div>
            <p className="text-sm font-medium text-blue-300">Architecture multi-entités</p>
            <p className="text-xs text-[var(--blue-d)]/70 mt-0.5">
              Chaque entité dispose de son propre périmètre de données (comptabilité, RH, CRM...).
              Le NINEA est obligatoire pour la conformité fiscale sénégalaise.
            </p>
          </div>
        </div>
      </div>

      {/* Liste entités */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-10 gap-2">
          <div className="animate-spin w-8 h-8 border-2 border-[var(--blue)] border-t-transparent rounded-full" />
        </div>
      ) : (
        <div className="space-y-4">
          {(entities ?? []).map((entity: any) => (
            <Card key={entity.id} className="hover:border-[var(--bd2)] transition-colors">
              <CardContent className="py-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-semibold text-[var(--tx)]">{entity.name}</h3>
                      <Badge variant={entity.isActive ? 'success' : 'default'} size="sm">
                        {entity.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      {entity.isDefault && (
                        <Badge variant="purple" size="sm">Principale</Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      {[
                        { label: 'NINEA', value: entity.ninea ?? '—', mono: true },
                        { label: 'RCCM', value: entity.rccm ?? '—', mono: true },
                        { label: 'Email', value: entity.email ?? '—' },
                        { label: 'Téléphone', value: entity.phone ?? '—' },
                      ].map(({ label, value, mono }) => (
                        <div key={label}>
                          <p className="text-xs text-[var(--tx3)]">{label}</p>
                          <p className={`text-sm text-[var(--tx2)] mt-0.5 ${mono ? 'font-mono' : ''}`}>{value}</p>
                        </div>
                      ))}
                    </div>

                    {entity.address && (
                      <p className="text-xs text-[var(--tx3)] mt-3">{entity.address}</p>
                    )}
                  </div>

                  <div className="text-right text-xs text-[var(--tx3)] flex-shrink-0">
                    <p>Créée {formatDateTime(entity.createdAt)}</p>
                    <p className="mt-2 font-mono text-[var(--tx4)] text-xs truncate max-w-[160px]">{entity.id}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {(entities ?? []).length === 0 && (
            <div className="text-center py-10 text-[var(--tx3)] text-sm">
              Aucune entité configurée.
            </div>
          )}
        </div>
      )}

      {showCreateModal && (
        <CreateEntityModal onClose={() => { setShowCreateModal(false); queryClient.invalidateQueries({ queryKey: ['admin-entities'] }) }} />
      )}
    </PageContainer>
  )
}

function CreateEntityModal({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState({
    name: '', ninea: '', rccm: '', email: '', phone: '', address: '', currency: 'XOF',
  })
  const [error, setError] = useState('')

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  const create = useMutation({
    mutationFn: () => adminApi.createEntity(form),
    onSuccess: onClose,
    onError: (err: any) => setError(err?.response?.data?.message ?? 'Erreur'),
  })

  const fields = [
    { key: 'name', label: 'Raison sociale *', placeholder: 'NexusCorp SARL', required: true },
    { key: 'ninea', label: 'NINEA *', placeholder: '1234567AB (format sénégalais)', required: true },
    { key: 'rccm', label: 'RCCM', placeholder: 'SN-DKR-2024-B-12345' },
    { key: 'email', label: 'Email', placeholder: 'contact@nexuscorp.sn' },
    { key: 'phone', label: 'Téléphone', placeholder: '+221 33 000 00 00' },
    { key: 'address', label: 'Adresse', placeholder: 'Dakar, Sénégal' },
  ]

  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
      <div className="nx-card w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--bd)]">
          <h2 className="text-base font-semibold text-[var(--tx)]">Créer une entité</h2>
          <button onClick={onClose} className="text-[var(--tx3)] hover:text-[var(--tx)]">✕</button>
        </div>
        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {error && <div className="bg-[var(--red-bg)] border border-[rgba(239,68,68,0.25)] rounded-lg p-3 text-sm text-[var(--red-text)]">{error}</div>}

          {fields.map(({ key, label, placeholder }) => (
            <div key={key}>
              <label className="block text-xs font-medium text-[var(--tx3)] mb-1">{label}</label>
              <input
                value={(form as any)[key]}
                onChange={set(key)}
                placeholder={placeholder}
                className="w-full bg-white border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none focus:border-blue-500"
              />
            </div>
          ))}

          <div>
            <label className="block text-xs font-medium text-[var(--tx3)] mb-1">Devise</label>
            <select
              value={form.currency}
              onChange={set('currency')}
              className="w-full bg-white border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none focus:border-blue-500"
            >
              <option value="XOF">XOF — Franc CFA (UEMOA)</option>
              <option value="EUR">EUR — Euro</option>
              <option value="USD">USD — Dollar américain</option>
            </select>
            <p className="text-xs text-[var(--tx3)] mt-1">⚠️ Pour le Sénégal, utiliser XOF (UEMOA) — jamais XAF (CEMAC)</p>
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-[var(--bd)]">
          <Button variant="outline" className="flex-1" onClick={onClose}>Annuler</Button>
          <Button
            className="flex-1"
            onClick={() => create.mutate()}
            isLoading={create.isPending}
            disabled={!form.name || !form.ninea}
          >
            Créer l'entité
          </Button>
        </div>
      </div>
    </div>
  )
}
